/**
 * LocalStorage key for auth state change notifications
 * Using localStorage events allows communication across separate windows
 */
const AUTH_STATE_CHANGE_KEY = "inflo:auth-state-change";

/**
 * Event detail types for authentication events
 */
export interface AuthEventDetail {
  user?: any; // User object from oidc-client-ts
  timestamp: number;
  eventType: "login" | "logout" | "state-changed";
}

/**
 * Dispatches a notification when user logs in
 * Uses localStorage events to work across separate windows (Office dialogs)
 */
export const dispatchUserLoggedIn = (user?: any) => {
  const detail: AuthEventDetail = {
    user,
    timestamp: Date.now(),
    eventType: "login",
  };

  // Write to localStorage to trigger storage event in other windows
  localStorage.setItem(AUTH_STATE_CHANGE_KEY, JSON.stringify(detail));

  // Also dispatch custom event for same-window listeners
  const event = new CustomEvent<AuthEventDetail>("inflo:user-logged-in", {
    detail,
  });
  window.dispatchEvent(event);

  // Remove the item after a short delay to allow storage event to fire
  // This ensures the event will fire again if the same action happens
  setTimeout(() => {
    localStorage.removeItem(AUTH_STATE_CHANGE_KEY);
  }, 100);
};

/**
 * Dispatches a notification when user logs out
 * Uses localStorage events to work across separate windows (Office dialogs)
 */
export const dispatchUserLoggedOut = () => {
  const detail: AuthEventDetail = {
    timestamp: Date.now(),
    eventType: "logout",
  };

  // Write to localStorage to trigger storage event in other windows
  localStorage.setItem(AUTH_STATE_CHANGE_KEY, JSON.stringify(detail));

  // Also dispatch custom event for same-window listeners
  const event = new CustomEvent<AuthEventDetail>("inflo:user-logged-out", {
    detail,
  });
  window.dispatchEvent(event);

  // Remove the item after a short delay to allow storage event to fire
  setTimeout(() => {
    localStorage.removeItem(AUTH_STATE_CHANGE_KEY);
  }, 100);
};

/**
 * Dispatches a notification when auth state changes
 * Uses localStorage events to work across separate windows (Office dialogs)
 */
export const dispatchAuthStateChanged = (user?: any) => {
  const detail: AuthEventDetail = {
    user,
    timestamp: Date.now(),
    eventType: "state-changed",
  };

  // Write to localStorage to trigger storage event in other windows
  localStorage.setItem(AUTH_STATE_CHANGE_KEY, JSON.stringify(detail));

  // Also dispatch custom event for same-window listeners
  const event = new CustomEvent<AuthEventDetail>("inflo:auth-state-changed", {
    detail,
  });
  window.dispatchEvent(event);

  // Remove the item after a short delay to allow storage event to fire
  setTimeout(() => {
    localStorage.removeItem(AUTH_STATE_CHANGE_KEY);
  }, 100);
};
